import { useState } from 'react';
import Cropper, { MediaSize } from 'react-easy-crop';
import { useTranslation } from 'next-i18next';

import { Modal } from '@phoenix/design-system/frontend/ui/modal/modal';
import { Button } from '@phoenix/design-system/frontend/ui/button/button';

import { UploadImagePlaceholder } from '../images-gallery/upload-image-placeholder';
import { readFile } from './helpers';
import { useImageCropContext } from './use-image-crop-context';

const IMAGE_DESIRED_SIZE_IN_PX = 480;

export const ImageCrop = ({ onDone }) => {
  const { t } = useTranslation('shared');
  const [openModal, setOpenModal] = useState(false);

  const { image, setImage, zoom, setZoom, crop, setCrop, onCropComplete, getProcessedImage, resetStates } = useImageCropContext();

  const handleDone = async () => {
    onDone(await getProcessedImage());
    resetStates();
    setOpenModal(false);
  };

  const handleFileChange = async ({ target }) => {
    const file = target.files[0];
    const data = await readFile(file);
    setImage({ data, fileName: file.name });
    setOpenModal(true);
    target.value = '';
  };

  const closeModal = () => {
    setOpenModal(false);
  };

  const onMediaLoaded = (mediaSize: MediaSize) => {
    if (mediaSize.width < IMAGE_DESIRED_SIZE_IN_PX) {
      setZoom(IMAGE_DESIRED_SIZE_IN_PX / mediaSize.width);
    } else {
      setZoom(IMAGE_DESIRED_SIZE_IN_PX / mediaSize.height);
    }
  };

  return (
    <>
      <input
        type="file"
        onChange={handleFileChange}
        className="hidden"
        id="customImageInput"
        accept="image/gif,image/png,image/jpeg,image/webp"
      />
      <button
        aria-label={t('shared_imageUpload_button_upload')}
        className="focus-visible:outline outline-primary-black outline-2 outline-offset-2"
        onKeyDown={(e) => e.key === 'Enter' && document.getElementById('customImageInput')?.click()}
      >
        <label htmlFor="customImageInput">
          <UploadImagePlaceholder />
        </label>
      </button>
      <Modal
        dialogIsOpen={openModal}
        closeDialog={closeModal}
        customSizeClassName="mobileOnly:mt-0 mobileOnly:h-screen max-w-3xl"
        closeOnClickOutside={false}
        contentClassName="pt-14"
      >
        <Cropper
          aspect={1}
          image={image?.data}
          zoom={zoom}
          crop={crop}
          onCropChange={setCrop}
          onCropComplete={onCropComplete}
          cropSize={{ width: IMAGE_DESIRED_SIZE_IN_PX, height: IMAGE_DESIRED_SIZE_IN_PX }}
          style={{
            containerStyle: {
              position: 'relative',
              height: `${IMAGE_DESIRED_SIZE_IN_PX}px`,
              width: '100%',
              maxWidth: '100%',
            },
            cropAreaStyle: {
              width: '100%',
              height: 'auto',
            },
          }}
          onMediaLoaded={onMediaLoaded}
        />
        <div className="mt-6 flex gap-6">
          <Button btnType="white_default" className="w-full" onClick={handleDone} data-testid="save-image-button">
            {t('common_form_button_save')}
          </Button>
          <Button btnType="white_outline" onClick={closeModal}>
            {t('common_form_button_cancel')}
          </Button>
        </div>
      </Modal>
    </>
  );
};
